import { useEffect, useState } from 'react'

export type CategorySeo = {
  description?: string
  extraText?: string
  pageTitle?: string
  metaDescription?: string
}

declare global {
  interface Window {
    MIDAT_CATEGORY_SEO_BY_PATH?: Record<string, CategorySeo>
    MIDAT_CATEGORY_SEO_LIST?: unknown
  }
}

const SCRIPT_ID = 'midat-category-seo'
const SCRIPT_SRC =
  'https://codex.websiteforge.net/MidAt-category-seo/visible-category-seo.js'

function readSeo(path: string): CategorySeo | null {
  if (typeof window === 'undefined') return null
  const map = window.MIDAT_CATEGORY_SEO_BY_PATH
  if (!map) return null
  return map[path] ?? null
}

function ensureScript() {
  if (typeof document === 'undefined') return
  if (document.getElementById(SCRIPT_ID)) return
  const el = document.createElement('script')
  el.id = SCRIPT_ID
  el.src = SCRIPT_SRC
  el.async = true
  document.head.appendChild(el)
}

/**
 * Loads the external category SEO data file (sets window globals) and returns
 * the route-specific SEO entry once available. Returns null until loaded so
 * callers can render a clean fallback. Also syncs document.title and the
 * meta description for matched routes, restoring the previous values on unmount.
 */
export function useCategorySeo(
  path: string,
  override?: CategorySeo,
): CategorySeo | null {
  const [loaded, setLoaded] = useState<CategorySeo | null>(() => readSeo(path))

  useEffect(() => {
    let cancelled = false
    ensureScript()

    // Resolve immediately if already present, otherwise poll until loaded.
    const existing = readSeo(path)
    if (existing) {
      setLoaded(existing)
    } else {
      setLoaded(null)
      let attempts = 0
      const timer = window.setInterval(() => {
        attempts += 1
        const found = readSeo(path)
        if (found && !cancelled) {
          setLoaded(found)
          window.clearInterval(timer)
        } else if (attempts > 50) {
          // ~10s ceiling; keep clean fallback if data never arrives.
          window.clearInterval(timer)
        }
      }, 200)
      return () => {
        cancelled = true
        window.clearInterval(timer)
      }
    }
  }, [path])

  // Local overrides win over the externally loaded entry so renamed
  // categories never surface legacy titles, descriptions, or product terms.
  const seo: CategorySeo | null =
    override || loaded
      ? {
          ...(loaded || {}),
          ...(override || {}),
        }
      : null

  const overrideKey = override ? JSON.stringify(override) : ''

  // Sync document title + meta description for matched routes.
  useEffect(() => {
    if (!seo) return
    const previousTitle = document.title
    if (seo.pageTitle) document.title = seo.pageTitle

    let metaEl = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    )
    let created = false
    let previousContent: string | null = null
    if (seo.metaDescription) {
      if (!metaEl) {
        metaEl = document.createElement('meta')
        metaEl.setAttribute('name', 'description')
        document.head.appendChild(metaEl)
        created = true
      } else {
        previousContent = metaEl.getAttribute('content')
      }
      metaEl.setAttribute('content', seo.metaDescription)
    }

    return () => {
      document.title = previousTitle
      if (metaEl) {
        if (created) {
          metaEl.remove()
        } else if (previousContent !== null) {
          metaEl.setAttribute('content', previousContent)
        }
      }
    }
  }, [loaded, overrideKey])

  return seo
}
